import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import FileLimitPopup, { FileLimitPopupMode } from './FileLimitPopup'

type FileLimitPopupState = {
    files: File[];
    mode: FileLimitPopupMode;
    onConfirm: (files: File[]) => void;
    onCancel: () => void;
};

type OpenFileLimitPopupArgs = {
    files: File[];
    mode: FileLimitPopupMode;
    onConfirm: (files: File[]) => void;
    onCancel?: () => void;
};

// Lets a caller (e.g. AddNewPopup) hand off an over-limit batch of files for
// the user to trim down, without having to lift that batch into
// WorkspaceContext. Portals into #work-space so it renders centered over the
// whole workspace regardless of where the calling component lives in the tree.
export function useFileLimitPopup() {
    const [state, setState] = useState<FileLimitPopupState | null>(null);

    const closeFileLimitPopup = useCallback(() => setState(null), []);

    const openFileLimitPopup = useCallback(({ files, mode, onConfirm, onCancel }: OpenFileLimitPopupArgs) => {
        setState({
            files,
            mode,
            onConfirm: (remaining: File[]) => {
                setState(null);
                onConfirm(remaining);
            },
            onCancel: () => {
                setState(null);
                onCancel?.();
            },
        });
    }, []);

    const portalTarget = state && typeof document !== 'undefined'
        ? document.getElementById('work-space')
        : null;

    const fileLimitPopupNode = state && portalTarget
        ? createPortal(
            <div className="file-limit-popup-portal">
                <FileLimitPopup
                    files={state.files}
                    mode={state.mode}
                    onConfirm={state.onConfirm}
                    onCancel={state.onCancel}
                />
            </div>,
            portalTarget
        )
        : null;

    return { openFileLimitPopup, closeFileLimitPopup, isFileLimitPopupOpen: !!state, fileLimitPopupNode };
}
